import { Fragment, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useGetUserDataQuery } from '@crm/entities/user';
import ChevronDown from '@crm/shared/assets/icons/chevrone-down.svg?react';
import { cn } from '@crm/shared/lib';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    SvgIcon,
} from '@crm/shared/ui';
import { Spinner } from '@crm/shared/ui/Spinner';
import { Avatar } from '@shared/ui';

import { ProfileMenuItem } from './ProfileMenuItem';
import { TariffData } from './TariffData';
import { profileData } from '../model/profile-data';

export const UserProfileMenu = () => {
    const { t } = useTranslation();
    const { data, isPending } = useGetUserDataQuery();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger
                aria-label={t('crm.userProfileMenu.triggerAriaLabel')}
                className="group flex cursor-pointer justify-center rounded-xs pr-3"
            >
                {isPending ? (
                    <Spinner className="size-6.75" />
                ) : (
                    <Avatar
                        classes="smetter-css"
                        avatarUrl={data?.user.avatarUrl ?? ''}
                        width={27}
                        height={27}
                    />
                )}
                <div className="flex items-center gap-2.5">
                    <span
                        className={cn(
                            'ml-[10px] max-w-40 truncate text-xs text-smtr-main-black-100/50',
                            isPending && 'invisible'
                        )}
                    >
                        {data?.user.name ?? t('crm.userProfileMenu.unknownName')}
                    </span>
                    <SvgIcon
                        icon={ChevronDown}
                        className="size-2.75 text-smtr-main-black-100 transition-transform group-data-popup-open:-rotate-180"
                    />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                sideOffset={18}
                className="m-0 flex w-52.5 list-none flex-col rounded-none p-0 shadow-[0_0_15px_0_rgba(0,0,0,0.5)]"
            >
                {data?.tariff ? (
                    <TariffData tariffData={data.tariff} onPaymentOpen={() => setIsOpen(false)} />
                ) : (
                    <div className="px-[13px] py-[11px] text-xs">
                        {t('crm.userProfileMenu.unknownTariff')}
                    </div>
                )}
                <DropdownMenuSeparator className="mx-0 my-0 bg-smtr-border-100" />

                {profileData.map((item) => (
                    <Fragment key={item.id}>
                        <DropdownMenuItem
                            render={(props) => {
                                if (item.route) {
                                    return (
                                        <Link {...props} to={item.route}>
                                            {props.children}
                                        </Link>
                                    );
                                }

                                if (item.href) {
                                    return (
                                        <a
                                            {...props}
                                            href={item.href}
                                            target={item.openInNewTab ? '_blank' : undefined}
                                            rel={item.openInNewTab ? 'noreferrer' : undefined}
                                        >
                                            {props.children}
                                        </a>
                                    );
                                }

                                return <div {...props}>{props.children}</div>;
                            }}
                            className={cn(
                                'p-0',
                                (item.route || item.href) &&
                                    'text-smtr-main-black-100 no-underline hover:no-underline'
                            )}
                        >
                            <ProfileMenuItem textContent={t(item.textKey)} icon={item.icon} />
                        </DropdownMenuItem>
                        {item.separatorAfter && (
                            <DropdownMenuSeparator className="mx-0 my-0 bg-smtr-border-100" />
                        )}
                    </Fragment>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
